import { StyleSheet, Text, View } from '@react-pdf/renderer';
import { extractResumeDataDocumentSection } from "../../../hooks/useResumeDocumentSection";
import { extractResumeProperties } from "../../../hooks/useResumeProperties";
import { calculateTotalExperienceDuration } from "../../../utils/calculateDatesDifference";
import { formatDateCssf } from "../../../utils/formatDate";
import { parseISO } from 'date-fns';

export default function CssfPdfExperiencesSection( { resumeData, translations } ) {
    const { experiences, totalExperienceDuration } = extractResumeDataDocumentSection( resumeData );
    const { isAnonymous, isExperienceHidden } = extractResumeProperties( resumeData );
    const documentLanguage = 'fr'; // Forced for CSSF

    if ( isExperienceHidden ) {
        return null;
    }

    const {
        years: displayYears,
        months: displayMonths,
    } = calculateTotalExperienceDuration( experiences, totalExperienceDuration );

    const styles = StyleSheet.create( {
        section: {
            flexDirection: 'row',
            marginBottom: '10px',
            fontSize: '10pt',
        },
        sectionCompetence: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            marginBottom: '8px',
        },
        textStart: {
            fontFamily: "Verdana, sans-serif",
            fontSize: "9pt",
            textAlign: "right",
            color: "#009999",
            margin: 0,
            marginTop: '4px',
        },
        leftColumn: {
            width: '30%',
            justifyContent: "flex-end",
            alignItems: "flex-end",
        },
        rightColumn: {
            width: '70%',
            marginLeft: '12px',
        },
    } );

    return (
        <>
            <View style={ { ...styles.section, marginBottom: 0 } }>
                <View style={ styles.leftColumn }>
                    <Text style={ {
                        ...styles.textStart,
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        display: "flex"
                    } }>
                        EXPÉRIENCE{ "\n" }PROFESSIONNELLE
                    </Text>
                </View>
                <View style={ {
                    ...styles.rightColumn,
                    width: "70%",
                    justifyContent: "flex-end",
                    alignItems: "flex-end"
                } }>
                    <View style={ {
                        width: "100%",
                        height: 5,
                        backgroundColor: "#009999",
                        marginVertical: 4
                    } }/>
                </View>
            </View>
            <View style={ {
                ...styles.section,
                marginBottom: 4
            } }>
                <View style={ styles.leftColumn }>
                    <Text style={ {
                        ...styles.textStart,
                        textTransform: "uppercase"
                    } }>
                        Nombre d’année d’expérience
                    </Text>
                </View>
                <View style={ styles.rightColumn }>
                    <Text style={ {
                        ...styles.textStart,
                        textAlign: 'start',
                        fontSize: "11px",
                        color: "#3F3A38",
                        marginTop: '8px'
                    } }>
                        { translations.pdf.workExperienceTemplate
                            .replace( "{years}", displayYears )
                            .replace( "{months}", displayMonths )
                            .replace( "(s)", 's' ) }
                    </Text>
                </View>
            </View>
            { !isExperienceHidden && (
                <>
                    { experiences
                        ?.sort((a, b) => parseISO(b?.from) - parseISO(a?.from))
                        .filter( ( experience ) => experience )
                        .map( ( experience, index ) => {
                            const validRoles = Array.isArray( experience.roles )
                                ? experience.roles.filter( role => role?.text && role.text.trim() !== '' )
                                : [];
                            return (
                                <View key={ `experience-${ index }` }
                                      style={ {
                                          width: "100%",
                                          display: "flex",
                                          flexDirection: "row"
                                      } }>
                                    <View style={ {
                                        ...styles.leftColumn,
                                        justifyContent: "flex-start",
                                        alignItems: "flex-end"
                                    } }>
                                        <Text style={ {
                                            ...styles.textStart,
                                            marginBottom: 8
                                        } }>
                                            { experience.from && ( experience.to || experience.isCurrent )
                                                ? 'De ' + formatDateCssf( experience.from, documentLanguage ) + ' à ' + ( experience.isCurrent ? translations.pdf.isCurrentLabel : formatDateCssf( experience.to, documentLanguage ) )
                                                : 'Depuis ' + formatDateCssf( experience.from, documentLanguage ) }
                                        </Text>
                                    </View>
                                    <View style={ styles.rightColumn }>
                                            <View style={ { width: "100%" } }>
                                                <View style={ styles.sectionCompetence }>
                                                    <Text style={ {
                                                        color: "#3F3A38",
                                                        fontSize: "11px",
                                                        fontFamily: "Verdana, sans-serif"
                                                    } }>
                                                        { experience.jobPosition }
                                                    </Text>
                                                </View>
                                                { experience.activitySector?.translations?.[ documentLanguage ] && (
                                                    <Text style={ {
                                                        color: "#3F3A38",
                                                        fontSize: "9px",
                                                        fontFamily: "Verdana, sans-serif",
                                                        marginBottom: 4
                                                    } }>
                                                        { translations.experiencesSection.activitySectorLabel }{ ' ' }
                                                        { experience.activitySector.translations[ documentLanguage ] }
                                                    </Text>
                                                ) }
                                                { experience.contractType?.translations?.[ documentLanguage ] && (
                                                    <Text style={ {
                                                        color: "#3F3A38",
                                                        fontSize: "9px",
                                                        fontFamily: "Verdana, sans-serif",
                                                        marginBottom: 4
                                                    } }>
                                                        { translations.experiencesSection.contractTypeLabel }{ ' ' }
                                                        { experience.contractType.translations[ documentLanguage ] }
                                                    </Text>
                                                ) }
                                            { validRoles.length > 0 && (
                                                <>
                                                    <Text
                                                        style={ {
                                                            color: "#3F3A38",
                                                            fontSize: "9px",
                                                            fontFamily: "Verdana, sans-serif"
                                                        } }>
                                                        { translations.pdf.rolesLabel }
                                                    </Text>
                                                    <View>
                                                        { validRoles
                                                            .sort( ( a, b ) => a.order - b.order )
                                                            .map( ( role, roleIndex ) => (
                                                                <View key={ roleIndex }
                                                                      style={ {
                                                                          flexDirection: "row",
                                                                          fontFamily: "Verdana, sans-serif",
                                                                          fontSize: "9pt",
                                                                          color: "#3F3A38",
                                                                      } }>
                                                                    <Text style={ { marginHorizontal: '8px' } }>-</Text>
                                                                    <Text>{ role.text }</Text>
                                                                </View>
                                                            ) ) }
                                                    </View>
                                                </>
                                            ) }
                                                { experience.context && experience.context.trim() !== "" && (
                                                    <Text style={ {
                                                        color: "#3F3A38",
                                                        fontSize: "9px",
                                                        fontFamily: "Verdana, sans-serif",
                                                        marginBottom: 8
                                                    } }>
                                                        { experience.context }
                                                    </Text>
                                                ) }
                                                { experience.achievement && experience.achievement.trim() !== "" && (
                                                    <Text style={ {
                                                        color: "#3F3A38",
                                                        fontSize: "9px",
                                                        fontFamily: "Verdana, sans-serif",
                                                        marginBottom: 8
                                                    } }>
                                                        { translations.pdf.achievementsLabel }{ ' ' }
                                                        { experience.achievement }
                                                    </Text>
                                                ) }
                                            { Array.isArray( experience.actions )
                                                && experience.actions.some( action => action?.text && action.text.trim() !== '' )
                                                && (
                                                <>
                                                    <Text
                                                        style={ {
                                                            color: "#3F3A38",
                                                            fontSize: "9px",
                                                            fontFamily: "Verdana, sans-serif"
                                                        } }>
                                                        { translations.pdf.actionsLabel }
                                                    </Text>
                                                    <View>
                                                        { experience.actions
                                                            .filter( action => action?.text && action.text.trim() )
                                                            .map( ( action, actionIndex ) => (
                                                                <View key={ actionIndex }
                                                                      style={ {
                                                                          flexDirection: "row",
                                                                          fontFamily: "Verdana, sans-serif",
                                                                          fontSize: "9pt",
                                                                          color: "#3F3A38",
                                                                      } }>
                                                                    <Text style={ { marginHorizontal: '8px' } }>-</Text>
                                                                    <Text>{ action.text }</Text>
                                                                </View>
                                                            ) ) }
                                                    </View>
                                                </>
                                            ) }
                                            { experience.technicalEnvironment && Array.isArray( experience.technicalEnvironment ) && experience.technicalEnvironment.length > 0 && (
                                                <>
                                                    <Text style={ {
                                                        color: "#3F3A38",
                                                        fontSize: "9px",
                                                        fontFamily: "Verdana, sans-serif",
                                                        marginBottom: 8,
                                                        marginTop: 8
                                                    } }>
                                                        { translations.pdf.technicalEnvironmentLabel }
                                                        { ' ' }
                                                    { experience.technicalEnvironment.map( skill => skill?.translations?.[ documentLanguage ] || skill ).join( ", " ) }
                                                </Text>
                                            </>
                                        ) }
                                            { experience.isConsultingMission
                                                ? (
                                                    <Text style={ {
                                                        color: "#3F3A38",
                                                        fontSize: "9px",
                                                        fontFamily: "Verdana, sans-serif",
                                                        marginBottom: 8
                                                    } }>
                                                        En mission chez&nbsp;
                                                        <Text style={ { fontWeight: "bold" } }>
                                                            { experience.client }
                                                        </Text>
                                                    </Text>
                                                )
                                                : (
                                                    <Text style={ {
                                                        color: "#3F3A38",
                                                        fontSize: "9px",
                                                        fontFamily: "Verdana, sans-serif",
                                                        marginBottom: 8
                                                    } }>
                                                        { isAnonymous ? translations.pdf.employerLabel : experience.employer }
                                                    </Text>
                                                ) }
                                        </View>
                                    </View>
                                </View>
                            );
                        } ) }
                </>
            ) }
        </>
    );
}
